import { parsePhoneNumber } from "utils/Haim/phoneNumbersUtils";
import { set, addDays, startOfDay, roundToNearestMinutes, addHours } from "date-fns";
import type {
  CalendarEventAttendee,
  CalendarEventAttendeeSource,
  CalendarEventType,
  RealEstateContact
} from "../../@types/sharedSchema";

type StartDateAndEndDate = {
  start: Date;
  end: Date;
};

export function getMeetingStartEndDate(date?: Date): StartDateAndEndDate {
  const targetDate = date ?? new Date();

  const start = roundToNearestMinutes(targetDate, !date && { nearestTo: 15 });
  const end = addHours(start, 1);

  return { start, end };
}

export function getTaskStartEndDate(date?: Date): StartDateAndEndDate {
  const targetDate = date ?? new Date();

  const startOfDayNowDate = startOfDay(targetDate);
  const tomorrowStartOfDay = startOfDay(addDays(targetDate, 1));

  return {
    start: startOfDayNowDate,
    end: tomorrowStartOfDay
  };
}

export function adjustTaskTimeForToday(startDate: Date, endDate: Date): StartDateAndEndDate {
  const today = new Date();

  const start = set(today, {
    hours: startDate.getHours(),
    minutes: startDate.getMinutes(),
    seconds: startDate.getSeconds(),
    milliseconds: startDate.getMilliseconds()
  });

  const end = set(today, {
    hours: endDate.getHours(),
    minutes: endDate.getMinutes(),
    seconds: endDate.getSeconds(),
    milliseconds: endDate.getMilliseconds()
  });

  return { start, end };
}

/**
 * The `source` values a Google pull stamps on an attendee it found by itself: a phone number
 * in the event's text (`description`) or a Google guest (`google`). `manual` is not here: it
 * is what an attendee WITHOUT a stamp is, and nothing writes it.
 *
 * A `Record`, so a source added to the shared type does not compile until it is decided here.
 */
const AUTO_DETECTED_ATTENDEE_SOURCES: Record<
  Exclude<CalendarEventAttendeeSource, "manual">,
  true
> = {
  description: true,
  google: true
};

/**
 * Every key an attendee can hold on its way through the form: as stored
 * (`CalendarEventAttendee`), or after `parseAttendeesToContacts` gave it the picker's contact
 * shape. A `Record` over the shared type, so a new attendee field does not compile until it
 * is listed: an attendee with a key missing from here would silently lose its `source`.
 */
const ATTENDEE_SHAPED_KEYS: Record<
  keyof CalendarEventAttendee | "id" | "callerFirstName" | "callerLastName",
  true
> = {
  firstName: true,
  lastName: true,
  phoneNumberObj: true,
  phoneNumberInternational: true,
  email: true,
  source: true,
  id: true,
  callerFirstName: true,
  callerLastName: true
};

const hasOwnKey = (record: object, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(record, key);

/**
 * The stamp of an attendee the realtor did NOT pick, to be kept across a save.
 *
 * The server sends the event's `clientNote` only to attendees without such a stamp, and the
 * Google pull keeps a stamp-less attendee for good. The form rewrites every attendee on every
 * save, so dropping the stamp here turns a seller found in a pasted listing into someone the
 * realtor picked: they receive the note written for the buyer, and nothing ever undoes it.
 *
 * Two conditions, both needed:
 *  - the VALUE is one of the two stamps. Anything else (absent, `manual`, a foreign string)
 *    is no stamp: someone the realtor added in the form must stay without one;
 *  - the ENTRY is an attendee, not a CRM contact. A contact document is free-form (the lead
 *    webhook spreads its request body onto it), so a contact can carry a `source` of its own
 *    that means "where the lead came from". Read as an attendee stamp it would cost a picked
 *    contact the note, and the next Google pull would drop them from the meeting.
 */
function keptAttendeeSource(entry: object): CalendarEventAttendeeSource | undefined {
  const { source } = entry as { source?: unknown };

  if (typeof source !== "string" || !hasOwnKey(AUTO_DETECTED_ATTENDEE_SOURCES, source)) {
    return undefined;
  }

  const isAttendeeShaped = Object.keys(entry).every((key) => hasOwnKey(ATTENDEE_SHAPED_KEYS, key));

  return isAttendeeShaped ? (source as CalendarEventAttendeeSource) : undefined;
}

export function parseContactsToAttendees(
  attendees: CalendarEventAttendee[] | RealEstateContact[]
): CalendarEventAttendee[] {
  type MergedAttendeeAndContact = RealEstateContact & CalendarEventAttendee;

  const parsedContactsToAttendees = attendees.map((entry: MergedAttendeeAndContact) => {
    const { id, lastName, firstName, callerLastName, callerFirstName, phoneNumberInternational } =
      entry;

    let parsedAttendee: CalendarEventAttendee = {
      lastName,
      firstName,
      phoneNumberInternational
    };

    // A contact's document id IS its phone, so it is both the input and the fallback.
    const validPhoneNumber = parsePhoneNumber(id)?.format("E.164") ?? id;

    if (validPhoneNumber) {
      parsedAttendee.phoneNumberInternational = validPhoneNumber;
    }

    if (callerFirstName) {
      parsedAttendee.firstName = callerFirstName;
    }

    if (callerLastName) {
      parsedAttendee.lastName = callerLastName;
    }

    // `source` is the ONE extra field that survives a save, and only as an auto-detected
    // stamp on an existing attendee. Nothing else is carried (not `email`: an attendee with
    // an email is pushed to Google as a guest).
    const source = keptAttendeeSource(entry);

    if (source) {
      parsedAttendee.source = source;
    }

    return parsedAttendee;
  });

  return parsedContactsToAttendees;
}

export function parseAttendeesToContacts(attendees: CalendarEventAttendee[]): RealEstateContact[] {
  const parsedAttendeesToContacts = attendees.map(
    ({ firstName, lastName, phoneNumberInternational, ...rest }) => {
      let parsedContact = rest as RealEstateContact;

      if (phoneNumberInternational) {
        parsedContact.id = phoneNumberInternational;
      }

      if (firstName) {
        parsedContact.callerFirstName = firstName;
      }

      if (lastName) {
        parsedContact.callerLastName = lastName;
      }

      return parsedContact;
    }
  );

  return parsedAttendeesToContacts;
}

/**
 * The `EditContactDialog` seed behind "הוסף את … לרשימת הלקוחות" on an attendee card.
 *
 * The phone goes in `callerPhoneNumber` — CREATE mode's phone channel. The attendee carries it
 * in `id` (see `parseAttendeesToContacts`), which is the EDIT-mode source, so handing the
 * dialog the attendee itself opened it with an empty, required phone box. `id` stays unset on
 * purpose, as in `buildCallContactSeed`.
 *
 * Phone and names only: the attendee's own fields (`source`, `email`, `phoneNumberObj`) are
 * not contact fields and would ride into the create payload.
 */
export function buildAttendeeContactSeed(
  attendee: Pick<RealEstateContact, "id" | "callerFirstName" | "callerLastName">
): Partial<RealEstateContact> {
  const seed: Partial<RealEstateContact> = { callerPhoneNumber: attendee.id };

  if (attendee.callerFirstName) seed.callerFirstName = attendee.callerFirstName;
  if (attendee.callerLastName) seed.callerLastName = attendee.callerLastName;

  return seed;
}

// ----------------------------------------------------------------------
// The message to the invitees (`CalendarEvent.clientNote`).
//
// The ONLY text a realtor types that an attendee receives on WhatsApp; `description` stays
// internal. Everything below is pure on purpose: CalendarForm cannot be imported in jest,
// and these rules decide whether a note is offered, kept, replaced or cleared.
// ----------------------------------------------------------------------

/**
 * What the form knows about the server's rollout gate. `unknown` (not answered yet, or the
 * request failed) is NOT `off`: both hide the input on an event without a note, but only
 * `off` is an answer.
 */
export type CalendarClientNoteGate = "unknown" | "off" | "on";

type ClientNoteAttendees = CalendarEventAttendee[] | RealEstateContact[];

/**
 * While typing: the note travels as ONE WhatsApp template parameter, which cannot hold a
 * line break, so Enter, a pasted multi-line text and tabs become a space. Deliberately does
 * not trim or collapse spaces: that would eat the space the realtor has just typed.
 */
export function normalizeClientNoteInput(value: string): string {
  return (value ?? "").replace(/[\r\n\t\v\f\u0085\u2028\u2029]+/g, " ");
}

/** On save, and wherever a stored note is read: one paragraph, single spaces, trimmed. */
export function finalizeClientNote(value: unknown): string {
  if (typeof value !== "string") return "";

  return value.replace(/\s+/g, " ").trim();
}

/** A note that is only whitespace is no note. */
export function hasClientNote(value: unknown): boolean {
  return finalizeClientNote(value) !== "";
}

/**
 * Can a note on this event reach anyone? Only a meeting messages its attendees, and only
 * the ones with a phone the server can send to: a Google guest who came with an email only,
 * or a number that does not parse, receives nothing.
 *
 * And only the ones the realtor picked: an attendee the Google pull found by itself keeps its
 * `source` stamp across a save (`keptAttendeeSource`) and the server sends that person the
 * plain message, never the note. A meeting whose only reachable attendees are auto-detected
 * must not offer an input whose text nobody would receive.
 *
 * Takes the form's raw attendee values (contacts from the picker, or stored attendees) and
 * judges them the way they will be saved, through `parseContactsToAttendees`.
 */
export function canOfferClientNote({
  eventType,
  attendees
}: {
  eventType?: CalendarEventType;
  attendees?: ClientNoteAttendees;
}): boolean {
  if (eventType === "task") return false;

  return parseContactsToAttendees(attendees ?? []).some(
    ({ phoneNumberInternational, source }) =>
      !source && Boolean(parsePhoneNumber(phoneNumberInternational))
  );
}

/**
 * Is the input on screen right now?
 *
 * The gate only decides whether to OFFER a note on an event that has none. A note that is
 * already stored is always shown, editable and clearable: the server keeps sending it on
 * the next reminder or reschedule whatever the gate says, so the realtor must be able to
 * see it and take it off.
 */
export function shouldShowClientNoteInput({
  gate,
  hasStoredNote,
  eventType,
  attendees
}: {
  gate: CalendarClientNoteGate;
  hasStoredNote: boolean;
  eventType?: CalendarEventType;
  attendees?: ClientNoteAttendees;
}): boolean {
  return canOfferClientNote({ eventType, attendees }) && (gate === "on" || hasStoredNote);
}

/**
 * What a save does to `clientNote`.
 *
 * - The input was never on screen in this form session → `write: false`: leave the key out
 *   altogether, so a stored note survives an edit that could not show it.
 * - It was on screen and the note can still reach someone → write what the realtor typed.
 * - It was on screen and the event is now a task, or has nobody to send to → write `""`,
 *   so a note never lingers where it can no longer be seen.
 *
 * `""` and not `undefined`: the write is a Firestore merge with `ignoreUndefinedProperties`,
 * where `undefined` is skipped and cannot clear a stored note.
 *
 * Keyed on "was it ever shown", never on the gate at submit time: text the realtor typed
 * must not be dropped because the gate changed while the form was open.
 */
export function resolveClientNoteWrite({
  inputWasShown,
  eventType,
  attendees,
  value
}: {
  inputWasShown: boolean;
  eventType?: CalendarEventType;
  attendees?: ClientNoteAttendees;
  value?: string;
}): { write: boolean; value?: string } {
  if (!inputWasShown) return { write: false };

  if (!canOfferClientNote({ eventType, attendees })) return { write: true, value: "" };

  return { write: true, value: finalizeClientNote(value) };
}
